export interface IProject {
  id: number;
  title: string;
  description: string;
  technologies: string[];
  image: string;
  liveLink?: string;
  githubLink: string;
}

export interface ISkill {
  name: string;
  level: number; // 1-100
  category: 'frontend' | 'backend' | 'tools' | 'soft-skills';
}

export interface ICertification {
  title: string;
  issuer: string;
  date: string;
  description: string;
}

export const projects: IProject[] = [
  {
    id: 1,
    title: "Mental Health Platform",
    description: "A fullstack web platform supporting mental health journeys with a visual Mind Garden progress tool. Features secure authentication, real-time data sync, and intuitive UI/UX design.",
    technologies: ["Next.js", "Tailwind CSS", "PostgreSQL", "Node.js", "Prisma"],
    image: "/Dhefry Andirezha.jpg",
    githubLink: "https://github.com/Montfort28/Safe-Haven-Platform"
  },
  {
    id: 2,
    title: "Gaming Info & Discovery Website",
    description: "Built a gaming discovery platform that allows users to search for video games, view release dates, explore detailed descriptions, and read tips or tricks for each title. The interface is clean, responsive, and designed to mimic modern gaming databases.",
    technologies: ["React", "Next.js", "Tailwind CSS", "REST API", "Node.js"],
    image: "/Gaming_clone.jpg",
    githubLink: "https://github.com/Montfort28"
  },
  {
    id: 3,
    title: "Educational Institutions Landing Page",
    description: "Fully responsive landing page built from Figma design. Features smooth animations, carousel functionality, and modern interactive elements using Framer Motion.",
    technologies: ["Next.js", "TypeScript", "Framer Motion", "Tailwind CSS"],
    image: "/posinnove.jpg",
    liveLink: "https://posinnove-webpage.vercel.app/",
    githubLink: "https://github.com/Montfort28/posinnove-webpage"
  },
  {
    id: 4,
    title: "BouletteProof - BP Ticket System",
    description: "Enterprise ticketing system with Google OAuth integration, Slack notifications, and ClickUp task automation. Internship project (May - Aug 2024) showcasing production-level development skills.",
    technologies: ["Next.js", "PostgreSQL", "Prisma", "Google OAuth", "Slack API"],
    image: "/Admin Panel_ Your Ticket Management HQ (1).jpg",
    githubLink: "https://github.com/Montfort28"
  },
  {
    id: 5,
    title: "Tourism Website (Forever Young Tours)",
    description: "Developing and enhancing a production tourism platform with React and Next.js, focusing on scalable UI architecture, responsive design, and intuitive user experiences. Integrating third-party tourism and booking APIs, improving affiliate marketing with campaign, sales, and commission tracking, and optimizing the platform through continuous feedback and business-led improvements.",
    technologies: ["React", "Next.js", "APIs", "Affiliate Marketing", "Responsive Design"],
    image: "/forever young tours.png",
    liveLink: "https://iforeveryoungtours.com/",
    githubLink: "https://github.com/Montfort28"
  }
];

export const skills: ISkill[] = [
  // Frontend
  { name: "React", level: 85, category: "frontend" },
  { name: "Next.js", level: 90, category: "frontend" },
  { name: "Vue.js", level: 80, category: "frontend" },
  { name: "TypeScript", level: 85, category: "frontend" },
  { name: "Tailwind CSS", level: 95, category: "frontend" },

  // Backend
  { name: "Node.js", level: 80, category: "backend" },
  { name: "PostgreSQL", level: 82, category: "backend" },
  { name: "Prisma ORM", level: 85, category: "backend" },
  { name: "REST APIs", level: 85, category: "backend" },
  { name: "OAuth / Authentication", level: 80, category: "backend" },

  // Tools
  { name: "GitHub", level: 90, category: "tools" },
  { name: "VS Code", level: 92, category: "tools" },
  { name: "Figma", level: 80, category: "tools" },
  { name: "Git & Rebase", level: 85, category: "tools" },

  // Soft Skills
  { name: "Teamwork & Agile", level: 90, category: "soft-skills" },
  { name: "Communication", level: 88, category: "soft-skills" },
  { name: "Problem Solving", level: 92, category: "soft-skills" },
  { name: "Attention to Detail", level: 90, category: "soft-skills" }
];

export const certifications: ICertification[] = [
  {
    title: "Software Developer",
    issuer: "Bittwok, Kigali",
    date: "Nov 2025 – Present",
    description: "Contributing to the development of a tourism website built with PHP-based frontend pages. Implementing responsive UI components, improving layouts, and enhancing user experience. Integrating APIs and translating business requirements into functional features within a fast-moving startup environment."
  },
  {
    title: "Software Developer Internship",
    issuer: "BouletteProof",
    date: "May – Aug 2024",
    description: "Full internship experience working on production systems with Google OAuth, Slack integrations, and enterprise-level database management using PostgreSQL and Prisma. Collaborated with team on feature development and system optimization."
  }
];

export interface ITechnology {
  name: string;
  imagePath: string;
  color: string;
}

export const technologies: ITechnology[] = [
  { name: "React", imagePath: "/images/tech/react.png", color: "#61dafb" },
  { name: "Next.js", imagePath: "/images/tech/nextjs.png", color: "#000000" },
  { name: "Vue.js", imagePath: "/images/tech/vue.png", color: "#42b883" },
  { name: "TypeScript", imagePath: "/images/tech/typescript.png", color: "#3178c6" },
  { name: "Tailwind CSS", imagePath: "/images/tech/tailwindcss.png", color: "#38bdf8" },
  { name: "Node.js", imagePath: "/images/tech/nodejs.png", color: "#68a063" },
  { name: "PostgreSQL", imagePath: "/images/tech/postgres.png", color: "#336791" },
  { name: "Prisma", imagePath: "/images/tech/prisma.png", color: "#0c344b" },
  { name: "Figma", imagePath: "/images/tech/figma.png", color: "#f24e1e" },
  { name: "GitHub", imagePath: "/images/tech/github.png", color: "#181717" },
];

export const personalInfo = {
  name: "Mugisha Louis Marie De Montfort",
  title: "Software Developer",
  role: "Software Developer (React, Next.js)",
  email: "mugishamontfort28@gmail.com",
  phone: "+250 790 779 776",
  location: "Kigali, Rwanda",
  oneLiner: "I build fast, modern, and scalable web applications using Next.js and clean UI design principles.",
  bio: "Software developer focused on reliable web products, clear interfaces, and maintainable solutions with React, Next.js, and modern APIs.",
  about: `Software developer focused on building reliable web products with React, Next.js, and modern APIs. I combine thoughtful interface design with maintainable engineering practices to turn business requirements into clear, production-ready experiences.

I specialize in API integrations, authentication, responsive interfaces, and iterative product improvements. I work effectively in Agile environments and value clear communication, code quality, and dependable delivery.

My approach balances user needs, business goals, and technical quality throughout the development process.`,
  education: {
    degree: "Bachelor of Software Engineering",
    institution: "INES Ruhengeri",
    location: "Musanze, Rwanda",
    graduationDate: "October 2025"
  },
  social: {
    github: "https://github.com/Montfort28",
    linkedin: "https://www.linkedin.com/in/mugisha-montfort-5b9177346/",
    instagram: "https://www.instagram.com/mont_fort_/",
  },
  resume: "/Mugisha Montfort_RESUME.pdf",
  profileImage: "/Mugisha_Docile.jpg",
  references: [
    {
      name: "Mr. MUNYENTWARI Clement",
      title: "Professor of Software-Based Modules",
      institution: "INES Ruhengeri",
      email: "mclement@ines.ac.rw",
      phone: "+250 788 807 133"
    },
    {
      name: "Mrs. MBONIMANA Consolee",
      title: "Lecturer of Software-Based Modules",
      institution: "INES Ruhengeri",
      email: "mconsolee@ines.ac.rw",
      phone: "+250 785 642 109"
    }
  ]
};